export interface ScrollPositionCache {
  /** Where the page of the key was left, if that is remembered. Asking counts as using it. */
  get: (key: string) => number | undefined,
  /** Remembers where the page of the key was left, and forgets the one that has gone unused for longest if that is more than the limit */
  set: (key: string, position: number) => void,
  /** Forgets where the page of the key was left, if that is remembered */
  delete: (key: string) => void
}

/**
 * Remembers where pages were scrolled to when they were left, but only for the most recently used of them, so that it
 * doesn't grow for as long as the app stays open. A page that has been forgotten is shown at the top, like any other
 * page that has no position remembered.
 *
 * A Map keeps its keys in the order that they were added in, so the one that has gone unused for longest is always the
 * first. Using a key - setting it or getting it - adds it again, at the end.
 */
export const createScrollPositionCache = (limit: number): ScrollPositionCache => {
  const positions = new Map<string, number>();

  // adds the key at the end, whether or not it was there already
  const touch = (key: string, position: number) => {
    positions.delete(key);
    positions.set(key, position);
  };

  return {
    get: key => {
      const position = positions.get(key);
      if (position !== undefined) {
        touch(key, position);
      }
      return position;
    },
    set: (key, position) => {
      touch(key, position);
      if (positions.size > limit) {
        positions.delete(positions.keys().next().value);
      }
    },
    delete: key => {
      positions.delete(key);
    }
  };
};
